/**
 * Утилиты для безопасной работы с датами
 */

/**
 * Безопасно преобразует строку даты в timestamp
 * @param dateString Строка даты
 * @returns Timestamp в миллисекундах или текущее время, если дата невалидна
 */
export const getSafeTimestamp = (dateString: string | null | undefined): number => {
  if (!dateString) return Date.now();
  
  try {
    // Сначала пытаемся распарсить как ISO строку
    let date: Date;
    
    if (dateString.includes('T') && dateString.includes('Z')) {
      // ISO 8601 формат: "2024-01-01T12:00:00.000Z"
      date = new Date(dateString);
    } else if (dateString.includes('T')) {
      // ISO без Z: "2024-01-01T12:00:00.000"
      date = new Date(dateString + 'Z');
    } else {
      // Другие форматы
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateString}, using current time`);
      return Date.now();
    }
    
    return date.getTime();
  } catch (error) {
    console.warn(`Error parsing date: ${dateString}, using current time`, error);
    return Date.now();
  }
};

/**
 * Проверяет, является ли timestamp валидным
 * @param timestamp Timestamp для проверки
 * @returns true, если timestamp валиден
 */
export const isValidTimestamp = (timestamp: number): boolean => {
  return !isNaN(timestamp) && timestamp > 0 && timestamp <= Date.now() + 86400000; // +24 часа для будущих дат
};

/**
 * Форматирует timestamp в читаемый вид
 * @param timestamp Timestamp для форматирования
 * @returns Отформатированная строка даты или "Дата недоступна" если timestamp невалиден
 */
export const formatTimestamp = (timestamp: number): string => {
  if (!isValidTimestamp(timestamp)) {
    return 'Дата недоступна';
  }
  
  try {
    const date = new Date(timestamp);
    
    // Проверяем, что дата валидна
    if (isNaN(date.getTime())) {
      return 'Дата недоступна';
    }
    
    // Добавляем ведущие нули к числам меньше 10
    const padZero = (num: number) => num < 10 ? `0${num}` : num;
    
    const day = padZero(date.getDate());
    const month = padZero(date.getMonth() + 1); // getMonth() возвращает индекс с 0
    const year = date.getFullYear();
    const hours = padZero(date.getHours());
    const minutes = padZero(date.getMinutes());
    
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch (error) {
    console.warn('Error formatting timestamp:', error, timestamp);
    return 'Дата недоступна';
  }
};

/**
 * Вычисляет длительность между двумя timestamp'ами
 * @param startTime Время начала
 * @param endTime Время окончания (если не указано, используется текущее время)
 * @returns Строка с форматированной длительностью
 */
export const calculateDuration = (startTime: number, endTime?: number): string => {
  if (!isValidTimestamp(startTime)) {
    return '0 сек';
  }
  
  const end = endTime && isValidTimestamp(endTime) ? endTime : Date.now();
  const duration = end - startTime;
  
  if (duration < 0) {
    return '0 сек';
  }
  
  const seconds = Math.floor(duration / 1000);
  
  if (seconds < 60) {
    return `${seconds} сек`;
  } else {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes} мин ${remainingSeconds} сек`;
  }
};
